/* Product Features Image gallery behavior. */

class FeaturesImageGallery extends HTMLElement {
  constructor() {
    super();

    this.onClickBound = this.onClick.bind(this);
    this.onSlideChangeBound = this.onSlideChange.bind(this);
    this.onTabClickBound = this.onTabClick.bind(this);
    this.onActivateBound = this.activate.bind(this);
  }

  connectedCallback() {
    if (this.isConnectedToDocument) return;
    this.isConnectedToDocument = true;

    this.addEventListener("click", this.onClickBound);
    this.addEventListener("media-tab:activate", this.onActivateBound);
    this.bindSlider();
    this.bindMediaTabs();
    this.bindCounterVisibility();
    this.updateCounterVisibility();

    // Feature content is rendered in a hidden tab. Wait until the browser has
    // laid it out before measuring the slider and assigning thumbnail indexes.
    requestAnimationFrame(() => {
      this.bindSlider();
      this.bindMediaTabs();
      this.bindCounterVisibility();
      this.updateCounterVisibility();
    });
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClickBound);
    this.removeEventListener("media-tab:activate", this.onActivateBound);
    this.slider?.removeEventListener("slider:change", this.onSlideChangeBound);
    this.mediaTabs?.removeEventListener("click", this.onTabClickBound);
    this.counterVisibilityObserver?.disconnect();
    this.counterVisibilityObserver = null;
    this.isConnectedToDocument = false;
    this.sliderBound = false;
    this.mediaTabsBound = false;
  }

  get mediaGallery() {
    return this.closest("media-gallery");
  }

  get mediaTabs() {
    return this.mediaGallery?.querySelector("[data-features-image-tabs]");
  }

  get slider() {
    return this.querySelector("slider-element");
  }

  get thumbnails() {
    const slider = this.slider;
    return slider
      ? this.querySelector(`media-dots[aria-controls="${slider.id}"]`)
      : null;
  }

  get slides() {
    return Array.from(this.querySelectorAll(".features-image-gallery__slide"));
  }

  get counter() {
    return this.querySelector("[data-features-image-counter]");
  }

  updateCounterVisibility() {
    const counter = this.counter;
    if (!counter) return;

    const isActive = !this.closest(".media-tab-content.hidden");
    counter.style.display =
      isActive && this.slides.length > 1 ? "block" : "none";
  }

  bindSlider() {
    if (this.slider && !this.sliderBound) {
      this.slider.addEventListener("slider:change", this.onSlideChangeBound);
      this.sliderBound = true;
    }
  }

  bindMediaTabs() {
    if (this.mediaTabs && !this.mediaTabsBound) {
      this.mediaTabs.addEventListener("click", this.onTabClickBound);
      this.mediaTabsBound = true;
    }
  }

  bindCounterVisibility() {
    const tabContent = this.closest(".media-tab-content");
    if (!tabContent || this.counterVisibilityObserver) return;

    this.counterVisibilityObserver = new MutationObserver(() => {
      this.updateCounterVisibility();
    });
    this.counterVisibilityObserver.observe(tabContent, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  activate() {
    this.bindSlider();
    this.bindMediaTabs();
    this.bindCounterVisibility();

    requestAnimationFrame(() => {
      const slider = this.slider;
      if (!slider || this.closest(".hidden")) return;

      if (slider.currentPage === undefined) slider.init();
      slider.reset();

      const thumbnails = this.thumbnails;
      if (thumbnails) {
        thumbnails.reset();
        thumbnails.resetIndexes();
      }

      if (slider.itemsToShow.length > 1 && slider.itemOffset > 0) {
        // Keep the current scroll position when returning from another tab.
        // Calling select(1) here would always reset the Features gallery to
        // the first image.
        slider.update();
        slider.dispatchEventHandler();
      } else {
        // SliderElement calculates the page from itemOffset. Keep a single
        // configured image on page 1 instead of dividing by zero in update().
        slider.currentPage = 1;
        slider.updateButtons();
        slider.dispatchEventHandler();
      }

      this.updateCounterVisibility();
    });
  }

  onClick(event) {
    const trigger = event.target.closest("[data-features-image-lightbox]");
    if (!trigger || !this.contains(trigger)) return;

    event.preventDefault();
    const slide = trigger.closest(".features-image-gallery__slide");
    const index = this.slides.indexOf(slide);
    if (index >= 0) this.openLightbox(index);
  }

  onSlideChange(event) {
    const counter = this.querySelector("[data-features-image-current]");
    if (counter) counter.textContent = event.detail.currentPage || 1;
    this.updateCounterVisibility();
  }

  onTabClick(event) {
    const activeTab = event.target.closest(".media-tab-item");
    if (!activeTab || !this.mediaTabs?.contains(activeTab)) return;

    const targetId = activeTab.getAttribute("data-target");
    if (!targetId) return;

    this.mediaTabs.querySelectorAll(".media-tab-item").forEach((tab) => {
      tab.classList.toggle("active", tab === activeTab);
    });
    this.mediaGallery?.querySelectorAll(".media-tab-content").forEach((content) => {
      content.classList.toggle("hidden", content.id !== targetId);
    });
    this.mediaGallery
      ?.querySelectorAll("[data-media-tab-preview]")
      .forEach((preview) => {
        preview.hidden = activeTab.getAttribute("data-tab-type") === "features";
      });

    this.updateCounterVisibility();

    if (activeTab.getAttribute("data-tab-type") === "features") {
      this.activate();
    }
  }

  get photoswipe() {
    if (this._photoswipe) return this._photoswipe;

    const lightbox = new PhotoSwipeLightbox({
      arrowPrevSVG:
        '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      arrowNextSVG:
        '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      closeSVG:
        '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="m7.5 22.5 15-15m-15 0 15 15"/></svg>',
      bgOpacity: 1,
      pswpModule: () => import(getPhotoSwipeModuleUrl()),
      arrowPrevTitle: theme.strings.previous,
      arrowNextTitle: theme.strings.next,
      closeTitle: theme.strings.close,
      allowPanToNext: false,
      allowMouseDrag: true,
      wheelToZoom: false,
      returnFocus: true,
      zoom: false,
    });

    lightbox.addFilter("thumbEl", (_thumbEl, data) => data.thumbnailElement);
    lightbox.on("change", () => {
      this.slider?.select(lightbox.pswp.currIndex + 1, true);
    });
    lightbox.on("close", () => {
      const selectedSlide = this.slider?.selected(lightbox.pswp.currIndex);
      lightbox.pswp._lastActiveElement = selectedSlide
        ? selectedSlide.querySelector("button")
        : document.activeElement;
    });

    lightbox.init();
    return (this._photoswipe = lightbox);
  }

  openLightbox(index) {
    const dataSource = this.slides
      .map((slide) => {
        const image = slide.querySelector("img");
        if (!image) return null;

        return {
          thumbnailElement: image,
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute("width")),
          height: parseInt(image.getAttribute("height")),
          alt: image.alt,
          thumbCropped: true,
        };
      })
      .filter(Boolean);

    if (dataSource.length > 0) this.photoswipe.loadAndOpen(index, dataSource);
  }
}
if (!customElements.get("features-image-gallery")) {
  customElements.define("features-image-gallery", FeaturesImageGallery);
}
